/**
 * 字典緩存工具
 * 
 * 管理字典數據的本地緩存（使用 IndexedDB）
 */

import {
  initDB,
  getDictionary,
  setDictionary,
  getAllDictionaries,
  getMetadata,
  setMetadata,
  clearAllDictionaries,
  clearExpiredDictionaries,
  migrateFromLocalStorage
} from './indexedDB';

const VERSION_KEY = 'version';
const VERSION_MD5_KEY = 'versionMD5';
const VERSION_CHECK_TIMESTAMP_KEY = 'versionCheckTimestamp'; // 版本檢查時間戳
let migrationCompleted = false;

/**
 * 初始化並遷移資料（如果需要）
 */
const ensureInitialized = async () => {
  if (!migrationCompleted) {
    try {
      await initDB();
      // 嘗試從 localStorage 遷移資料
      await migrateFromLocalStorage();
      migrationCompleted = true;
    } catch (error) {
      console.error('初始化 IndexedDB 失敗:', error);
    }
  }
};

/**
 * 獲取緩存的版本號
 */
export const getCachedVersion = async () => {
  try {
    await ensureInitialized();
    return await getMetadata(VERSION_KEY);
  } catch (error) {
    console.error('Failed to get cached version:', error);
    return null;
  }
};

/**
 * 設置緩存的版本號
 */
export const setCachedVersion = async (version, versionMD5 = null) => {
  try {
    await ensureInitialized();
    const versionResult = await setMetadata(VERSION_KEY, version);
    if (versionMD5) {
      await setMetadata(VERSION_MD5_KEY, versionMD5);
    }
    return versionResult;
  } catch (error) {
    console.error('Failed to set cached version:', error);
    return false;
  }
};

/**
 * 獲取緩存的版本 MD5
 */
export const getCachedVersionMD5 = async () => {
  try {
    await ensureInitialized();
    return await getMetadata(VERSION_MD5_KEY);
  } catch (error) {
    console.error('Failed to get cached version MD5:', error);
    return null;
  }
};

/**
 * 獲取最後檢查版本號的時間戳
 */
export const getLastVersionCheckTimestamp = async () => {
  try {
    await ensureInitialized();
    const timestamp = await getMetadata(VERSION_CHECK_TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  } catch (error) {
    console.error('Failed to get last version check timestamp:', error);
    return null;
  }
};

/**
 * 設置最後檢查版本號的時間戳
 */
export const setLastVersionCheckTimestamp = async (timestamp = null) => {
  try {
    await ensureInitialized();
    const ts = timestamp || Date.now();
    return await setMetadata(VERSION_CHECK_TIMESTAMP_KEY, ts.toString());
  } catch (error) {
    console.error('Failed to set last version check timestamp:', error);
    return false;
  }
};

/**
 * 獲取緩存的字典數據
 */
export const getCachedDictionary = async (dictName) => {
  try {
    await ensureInitialized();
    return await getDictionary(dictName);
  } catch (error) {
    console.error(`Failed to get cached dictionary ${dictName}:`, error);
    return null;
  }
};

/**
 * 設置緩存的字典數據
 */
export const setCachedDictionary = async (dictName, data) => {
  try {
    await ensureInitialized();
    return await setDictionary(dictName, data);
  } catch (error) {
    console.error(`Failed to set cached dictionary ${dictName}:`, error);
    return false;
  }
};

/**
 * 獲取所有緩存的字典
 */
export const getAllCachedDictionaries = async () => {
  try {
    await ensureInitialized();
    return await getAllDictionaries();
  } catch (error) {
    console.error('Failed to get all cached dictionaries:', error);
    return {};
  }
};

/**
 * 清除舊的緩存
 */
export const clearOldCache = async () => {
  try {
    await ensureInitialized();
    return await clearExpiredDictionaries();
  } catch (error) {
    console.error('Failed to clear old cache:', error);
    return 0;
  }
};

/**
 * 清除所有字典緩存
 */
export const clearAllCache = async () => {
  try {
    await ensureInitialized();
    // 清除字典資料
    await clearAllDictionaries();
    // 清除版本號
    await setMetadata(VERSION_KEY, '');
    // 清除版本檢查時間戳
    await setMetadata(VERSION_CHECK_TIMESTAMP_KEY, '');
    return true;
  } catch (error) {
    console.error('Failed to clear all cache:', error);
    return false;
  }
};

